import * as React from "react"
import { router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { InlineField } from "@/components/ui/inline-edit"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { useInlineRecord } from "@/hooks/use-inline-record"
import type { PageProps } from "@/types/inertia"

export type TherapyData = {
  id?: number
  slug: string
  slug_es: string | null
  label: string
  nav_label_en: string
  nav_label_es: string
  blurb_en: string | null
  blurb_es: string | null
  meta_title: string
  meta_description: string
  status: string
  position: number
}

const TEXTAREA_CLASS =
  "w-full rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent"

function slugify(text: string) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

// The shell of one therapy landing page. Simpler than ConditionForm by
// design: every therapy is a full page, so there is no kind, no menu group
// and no parent to choose.
export function TherapyForm({
  therapy,
  actionUrl,
  method,
}: {
  therapy: TherapyData
  actionUrl: string
  method: "post" | "patch"
}) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const giselaUrl = props.brand_urls.gisela
  const isNew = method === "post"

  // An existing page saves a field at a time; a new one collects a draft for
  // one Create, since there is no row to PATCH into yet.
  const fields = useInlineRecord({
    record: {
      label: therapy.label,
      slug: therapy.slug,
      slug_es: therapy.slug_es ?? "",
      status: therapy.status,
      nav_label_en: therapy.nav_label_en,
      nav_label_es: therapy.nav_label_es,
      blurb_en: therapy.blurb_en ?? "",
      blurb_es: therapy.blurb_es ?? "",
      meta_title: therapy.meta_title,
      meta_description: therapy.meta_description,
      position: String(therapy.position),
    },
    url: actionUrl,
    param: "therapy",
    enabled: !isNew,
  })
  const v = fields.values

  const [slugEdited, setSlugEdited] = React.useState(!isNew)
  const [navEnEdited, setNavEnEdited] = React.useState(!isNew)
  const [metaTitleEdited, setMetaTitleEdited] = React.useState(!isNew)
  const [processing, setProcessing] = React.useState(false)

  // On a new page, the name fills in the address, nav label and search title
  // until those are edited by hand.
  function handleLabelChange(value: string) {
    const derived: Record<string, string> = { label: value }
    if (!slugEdited) derived.slug = slugify(value)
    if (!navEnEdited) derived.nav_label_en = value
    if (!metaTitleEdited) derived.meta_title = value ? `${value} | Gisela Ramirez` : ""
    fields.patch(derived)
  }

  const slugChanged = !isNew && v.slug !== therapy.slug

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    router.post(
      actionUrl,
      { therapy: { ...v, position: Number(v.position) } },
      { onFinish: () => setProcessing(false) },
    )
  }

  function handleDelete() {
    if (
      !window.confirm(
        `Delete the ${therapy.label} page? Its address will stop working and all its content will be removed. This cannot be undone.`,
      )
    )
      return
    router.delete(actionUrl)
  }

  const labelState = fields.state("label")
  const slugState = fields.state("slug")
  const slugEsState = fields.state("slug_es")
  const statusState = fields.state("status")
  const navEnState = fields.state("nav_label_en")
  const navEsState = fields.state("nav_label_es")
  const blurbEnState = fields.state("blurb_en")
  const blurbEsState = fields.state("blurb_es")
  const metaTitleState = fields.state("meta_title")
  const metaDescState = fields.state("meta_description")
  const positionState = fields.state("position", { serialize: (value) => Number(value) })

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <InlineField
        label="Therapy name"
        htmlFor="label"
        hint="Spell the name out — the acronym goes in brackets, as in the placeholder."
        error={errors.label}
        state={labelState}
      >
        <Input
          id="label"
          value={v.label}
          onChange={(e) => handleLabelChange(e.target.value)}
          onKeyDown={labelState.keys}
          required
          placeholder="e.g. Compassion Focused Therapy (CFT)"
          aria-invalid={!!errors.label}
        />
      </InlineField>

      <InlineField
        label="Web address"
        htmlFor="slug"
        hint={
          slugChanged && therapy.status === "published"
            ? "Careful: changing the address of a published page breaks links Google has already indexed."
            : undefined
        }
        error={errors.slug}
        state={slugState}
      >
        <Input
          id="slug"
          value={v.slug}
          onChange={(e) => {
            setSlugEdited(true)
            fields.set("slug", e.target.value)
          }}
          onKeyDown={slugState.keys}
          required
          placeholder="e.g. compassion-focused-therapy"
          aria-invalid={!!errors.slug}
        />
        {v.slug && (
          <p className="text-xs text-ink-muted">
            The page will live at{" "}
            <span className="font-medium text-ink-body">
              {giselaUrl}/{v.slug}
            </span>
          </p>
        )}
      </InlineField>

      <InlineField
        label="Spanish web address"
        htmlFor="slug_es"
        hint="Optional. With one, the Spanish page lives at its own Spanish address; without, it stays at /es plus the English one."
        error={errors.slug_es}
        state={slugEsState}
      >
        <Input
          id="slug_es"
          value={v.slug_es}
          onChange={(e) => fields.set("slug_es", e.target.value)}
          onKeyDown={slugEsState.keys}
          placeholder="e.g. terapia-centrada-en-la-compasion"
          aria-invalid={!!errors.slug_es}
        />
        {v.slug_es && (
          <p className="text-xs text-ink-muted">
            Spanish readers will find it at{" "}
            <span className="font-medium text-ink-body">
              {giselaUrl}/{v.slug_es}
            </span>
          </p>
        )}
      </InlineField>

      <InlineField
        label="Visibility"
        htmlFor="status"
        hint="Drafts are visible only to you (while logged in) and stay out of Google's sitemap — publish when the copy is ready."
        error={errors.status}
        state={statusState}
      >
        <Select id="status" value={v.status} onChange={(e) => fields.set("status", e.target.value)}>
          <option value="draft">Draft — hidden from visitors</option>
          <option value="published">Published — live on the site</option>
        </Select>
      </InlineField>

      <div className="grid gap-4 sm:grid-cols-2">
        <InlineField
          label="Link label (English)"
          htmlFor="nav_label_en"
          hint="Shown in the “Therapies I offer” menu, the footer and page lists."
          error={errors.nav_label_en}
          state={navEnState}
        >
          <Input
            id="nav_label_en"
            value={v.nav_label_en}
            onChange={(e) => {
              setNavEnEdited(true)
              fields.set("nav_label_en", e.target.value)
            }}
            onKeyDown={navEnState.keys}
            required
            aria-invalid={!!errors.nav_label_en}
          />
        </InlineField>

        <InlineField
          label="Link label (Spanish)"
          htmlFor="nav_label_es"
          error={errors.nav_label_es}
          state={navEsState}
        >
          <Input
            id="nav_label_es"
            value={v.nav_label_es}
            onChange={(e) => fields.set("nav_label_es", e.target.value)}
            onKeyDown={navEsState.keys}
            required
            placeholder="e.g. Terapia Centrada en la Compasión (CFT)"
            aria-invalid={!!errors.nav_label_es}
          />
        </InlineField>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <InlineField
          label="One-line description (English)"
          htmlFor="blurb_en"
          hint="Shown under this name on the “Therapies I offer” page."
          error={errors.blurb_en}
          state={blurbEnState}
        >
          <textarea
            id="blurb_en"
            value={v.blurb_en}
            onChange={(e) => fields.set("blurb_en", e.target.value)}
            onKeyDown={blurbEnState.multilineKeys}
            rows={2}
            className={TEXTAREA_CLASS}
            placeholder="What this therapy does, in one sentence…"
            aria-invalid={!!errors.blurb_en}
          />
        </InlineField>

        <InlineField
          label="One-line description (Spanish)"
          htmlFor="blurb_es"
          error={errors.blurb_es}
          state={blurbEsState}
        >
          <textarea
            id="blurb_es"
            value={v.blurb_es}
            onChange={(e) => fields.set("blurb_es", e.target.value)}
            onKeyDown={blurbEsState.multilineKeys}
            rows={2}
            className={TEXTAREA_CLASS}
          />
        </InlineField>
      </div>

      <InlineField
        label="Search title"
        htmlFor="meta_title"
        hint={`Shown as the page's title in Google. Keep it under 60 characters (currently ${v.meta_title.length}).`}
        error={errors.meta_title}
        state={metaTitleState}
      >
        <Input
          id="meta_title"
          value={v.meta_title}
          onChange={(e) => {
            setMetaTitleEdited(true)
            fields.set("meta_title", e.target.value)
          }}
          onKeyDown={metaTitleState.keys}
          required
          placeholder="e.g. Compassion Focused Therapy (CFT) | Gisela Ramirez"
          aria-invalid={!!errors.meta_title}
        />
      </InlineField>

      <InlineField
        label="Search description"
        htmlFor="meta_description"
        hint={`The snippet under the title in Google — 120–160 characters works best (currently ${v.meta_description.length}).`}
        error={errors.meta_description}
        state={metaDescState}
      >
        <textarea
          id="meta_description"
          value={v.meta_description}
          onChange={(e) => fields.set("meta_description", e.target.value)}
          onKeyDown={metaDescState.multilineKeys}
          required
          rows={3}
          className={TEXTAREA_CLASS}
          placeholder="What this therapy is and who it suits, written for someone scanning search results…"
          aria-invalid={!!errors.meta_description}
        />
      </InlineField>

      <InlineField
        label="Display order"
        htmlFor="position"
        hint="Lower numbers appear first in the menu."
        error={errors.position}
        state={positionState}
        className="max-w-40"
      >
        <Input
          id="position"
          type="number"
          value={v.position}
          onChange={(e) => fields.set("position", e.target.value)}
          onKeyDown={positionState.keys}
          min={0}
        />
      </InlineField>

      <div className="flex flex-wrap items-center gap-3 border-t border-hairline pt-6">
        {isNew ? (
          <>
            <Button type="submit" disabled={processing}>
              Create page
            </Button>
            <Button type="button" variant="ghost" onClick={() => window.history.back()}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-ink-muted">
              Each change is kept when you tick it — there's nothing else to save.
            </p>
            <Button type="button" variant="ghost" className="ml-auto text-danger-display" onClick={handleDelete}>
              Delete page
            </Button>
          </>
        )}
      </div>
    </form>
  )
}
